import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { RootState } from "../../app/store";
import {
  Question,
  QUESTIONNAIRE_IDS,
  QuestionnaireEntity,
} from "../../common/types/QuestionnaireEntity";
import {
  QuestionnaireResponse,
  ResponseItem,
} from "../../common/types/QuestionnaireResponse";
import {
  fetchQuestionnaires,
  fetchUserResponses,
  updateUserResponse,
} from "../APIservice";
import createDebouncedAsyncThunk from "../utils/createDebouncedAsyncThunk";
import {
  calculateCompletion,
  calculateQuestionnaireScore,
  getIsEnabled,
  makeAnswerObject,
} from "../utils/questionnaireUtils";

export type QuestionnaireAnswer = {
  questionId: string;
  questionType: string;
  patientId: string;
  value: {
    response?: string | number;
    multiSelect: (string | number)[];
    detailedResponse?: string;
  };
};

export type AnswerObject = { [key: string]: QuestionnaireAnswer | undefined };
export interface QuestionnaireState {
  questionnaires: QuestionnaireEntity[];
  responses: QuestionnaireResponse[];
  status: "idle" | "loading" | "failed";
  updateStatus: "idle" | "loading" | "failed";
  activeQuestionnaire?: string;
  answers: AnswerObject;
  currentQuestion?: Question;
}

export const initialState: QuestionnaireState = {
  questionnaires: [],
  responses: [],
  status: "idle",
  updateStatus: "idle",
  answers: {},
};

export type FetchQuestionnairesParams = {
  status?: string;
  client_id?: number;
  language?: string;
};

export const fetchQuestionnairesAsync = createAsyncThunk(
  "questionnaires/fetchQuestionnaires",
  async (params: FetchQuestionnairesParams, { getState }) => {
    const state = getState() as RootState;
    const response = await fetchQuestionnaires(state, params);
    return response.data.questionnaires.map(
      (elem: any): QuestionnaireEntity => {
        return elem.resource;
      }
    );
  }
);

export const fetchUserResponsesAsync = createAsyncThunk(
  "questionnaires/fetchUserResponses",
  async (_arg, { getState }) => {
    const state = getState() as RootState;
    const response = await fetchUserResponses(state as RootState);
    return response.data.questionnaireResponse.map(
      (elem: { resource: any }) => elem.resource
    );
  }
);

const debounceWait = 500;
export const updateUserResponseAsync = createDebouncedAsyncThunk(
  "questionnaires/updateUserResponse",
  async (_arg, { getState }) => {
    const state = getState() as RootState;
    const { data } = await updateUserResponse(state);
    return data;
  },
  debounceWait
);

export const questionnaireSlice = createSlice({
  name: "questionnaires",
  initialState,
  reducers: {
    reset: (state) => {
      state.questionnaires = [];
      state.responses = [];
      state.status = "idle";
      state.answers = {};
      return state;
    },
    updateHistoricalAnswers: (state) => {
      // clear historical answers for questions that are no longer enabled
      const currentQuestionnaire = state.questionnaires.find(
        (questionnaire) => questionnaire.id === state.activeQuestionnaire
      );

      const questionnaireResponse = state.responses.find(
        (response) => response.questionnaire === state?.activeQuestionnaire
      );
      const questionnaireResponseIndex = state.responses.findIndex(
        (response) => response.questionnaire === state?.activeQuestionnaire
      );

      const disabledQuestionIDs: string[] = [];

      // clear answers in state
      currentQuestionnaire?.item.forEach((question) => {
        const isQuestionEnabled = getIsEnabled(question, state.answers);
        if (!isQuestionEnabled) {
          disabledQuestionIDs.push(question.id);
          const answer = state.answers[question.id];
          if (answer) {
            state.answers[question.id] = {
              ...answer,
              value: {
                multiSelect: [],
              },
            };
          }
        }
      });

      // remove responses in state
      const updatedResponseItems = questionnaireResponse
        ? questionnaireResponse.item.reduce<ResponseItem[]>(
            (finalResponseItems, responseItem) => {
              if (!disabledQuestionIDs.includes(responseItem.id)) {
                finalResponseItems.push(responseItem);
              }
              return finalResponseItems;
            },
            []
          )
        : [];

      state.responses[questionnaireResponseIndex] = {
        ...state.responses[questionnaireResponseIndex],
        item: updatedResponseItems,
      };

      return state;
    },
    getNextQuestion: (state) => {
      const currentQuestionId = state.currentQuestion?.id;
      const currentQuestionnaire = state.questionnaires.find(
        (elem) => elem.id === state.activeQuestionnaire
      );
      const currentIndex =
        currentQuestionnaire?.item.findIndex(
          (elem) => elem.id === currentQuestionId
        ) ?? -1;

      if (
        currentIndex >= 0 &&
        currentQuestionnaire &&
        currentQuestionnaire.id &&
        currentQuestionnaire.item
      ) {
        // get next enabled question
        for (
          let i = currentIndex + 1;
          i < currentQuestionnaire.item.length;
          i++
        ) {
          const possibleNext = currentQuestionnaire.item[i];
          if (getIsEnabled(possibleNext, state.answers)) {
            state.currentQuestion = possibleNext;
            break;
          }
        }
      } else {
        // Pick the first one if undefined
        state.currentQuestion = currentQuestionnaire?.item[0];
      }
      return state;
    },
    getPreviousQuestion: (state) => {
      const currentQuestionId = state.currentQuestion?.id;
      const currentQuestionnaire = state.questionnaires.find(
        (elem) => elem.id === state.activeQuestionnaire
      );
      const currentIndex =
        currentQuestionnaire?.item.findIndex(
          (elem) => elem.id === currentQuestionId
        ) ?? -1;

      if (
        currentIndex >= 0 &&
        currentQuestionnaire &&
        currentQuestionnaire.id &&
        currentQuestionnaire.item
      ) {
        // get next enabled question
        if (currentIndex === 0) {
          state.currentQuestion = undefined;
        } else {
          for (let i = currentIndex - 1; i >= 0; i--) {
            const possibleNext = currentQuestionnaire.item[i];
            if (getIsEnabled(possibleNext, state.answers)) {
              state.currentQuestion = possibleNext;
              break;
            }
          }
        }
      } else {
        // Pick the first one if undefined
        state.currentQuestion = currentQuestionnaire?.item[0];
      }
      return state;
    },
    setActiveQuestionnaire: (state, action) => {
      state.activeQuestionnaire = action.payload;
      state.currentQuestion = undefined;
      // Set existing answers to state on question load
      const response = state.responses.find(
        (elem) => elem.questionnaire === action.payload
      );
      const questionnaire = state.questionnaires.find(
        (elem) => elem.id === action.payload
      );
      const answers: AnswerObject = {};
      if (questionnaire) {
        for (const question of questionnaire?.item) {
          const userResponse = response?.item.find(
            (elem) => elem.id === question.id
          );
          answers[question.id] = makeAnswerObject(question, userResponse);
        }
      }

      state.answers = answers;
      return state;
    },
    answerQuestion: (
      state,
      action: { payload: QuestionnaireAnswer; type: string }
    ) => {
      const answer = action.payload;
      state.answers[action.payload.questionId] = answer;

      // Coerce data to db shape and save to local state
      let response = state.responses.find(
        (elem) => elem.questionnaire === state.activeQuestionnaire
      );
      const responseIndex = state.responses.findIndex(
        (elem) => elem.questionnaire === state.activeQuestionnaire
      );
      if (!response) {
        response = {
          questionnaire: state.activeQuestionnaire as string,
          resourceType: "QuestionnaireResponse",
          item: [],
          status: "in-progress",
          questionnaireReference: {
            id: state.activeQuestionnaire as string,
            resourceType: "Questionnaire",
          },
          author: {
            id: action.payload.patientId,
            resourceType: "Patient",
          },
        };
      } else {
        response.status =
          calculateCompletion(state, response.questionnaire) === 100
            ? "completed"
            : "in-progress";
      }

      const modifiedQuestion = response?.item?.find(
        (elem) => elem.linkId === action.payload.questionId
      );
      const detailedResponse = action.payload.value.detailedResponse;
      const userAnswer: (string | number)[] = action.payload.value.response
        ? [action.payload.value.response]
        : action.payload.value.multiSelect;
      const responseItem: ResponseItem = modifiedQuestion
        ? modifiedQuestion
        : {
            id: action.payload.questionId,
            linkId: action.payload.questionId,
          };

      responseItem.answer = userAnswer.map((elem) => {
        return {
          value: {
            string: elem.toString(),
          },
        };
      });

      if (detailedResponse) {
        responseItem.item = [
          {
            id: action.payload.questionId + ".1",
            answer: [{ value: { string: detailedResponse } }],
            linkId: action.payload.questionId + ".1",
          },
        ];
      }
      // Clear detailed responses from questionnaire response when a user deselects a detailed response field
      if (!detailedResponse && responseItem.item) {
        responseItem.item = [];
      }

      if (!modifiedQuestion) {
        response?.item.push(responseItem);
      }

      const score = calculateQuestionnaireScore(state, response.questionnaire);
      response.score = score;
      if (responseIndex >= 0) {
        state.responses[responseIndex] = response;
      } else {
        state.responses.push(response);
      }

      return state;
    },
    resetQuestionnaire: (state) => {
      state.currentQuestion = undefined;
      state.activeQuestionnaire = undefined;
      state.answers = {};
      return state;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchQuestionnairesAsync.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchQuestionnairesAsync.fulfilled, (state, action) => {
        state.status = "idle";
        state.questionnaires = action.payload;
      })
      .addCase(fetchQuestionnairesAsync.rejected, (state, e) => {
        state.status = "failed";
      })
      .addCase(fetchUserResponsesAsync.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchUserResponsesAsync.fulfilled, (state, action) => {
        state.status = "idle";
        const responses: QuestionnaireResponse[] = action.payload;
        state.responses = responses;
      })
      .addCase(fetchUserResponsesAsync.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(updateUserResponseAsync.pending, (state) => {
        state.updateStatus = "loading";
      })
      .addCase(updateUserResponseAsync.fulfilled, (state, action) => {
        state.status = "idle";
        const response: QuestionnaireResponse = action.payload;
        const index = state.responses.findIndex(
          (value) => value.questionnaire === response.questionnaire
        );
        if (index >= 0) {
          state.responses[index] = action.payload;
        } else {
          state.responses.push(action.payload);
        }
      })
      .addCase(updateUserResponseAsync.rejected, (state) => {
        state.status = "failed";
        console.log("Failed to update");
      })
      .addCase("user/logout", (state) => {
        state.questionnaires = [];
        state.responses = [];
        state.status = "idle";
        state.answers = {};
      });
  },
});

export const selectQuestionnaires = (state: RootState) =>
  state.questionnaire.questionnaires;
export const selectQuestionnaireResponses = (state: RootState) =>
  state.questionnaire.responses;
export const selectQuestionnaireById = (state: RootState, id: string) => {
  return state.questionnaire.questionnaires.find((elem) =>
    id.includes(elem.originId)
  );
};
export const selectResponseByQuestionnaire = (
  state: RootState,
  id: string | undefined
) => state.questionnaire.responses.find((elem) => elem.questionnaire === id);
export const selectCurrentQuestion = (state: RootState) =>
  state.questionnaire.currentQuestion;
export const selectCurrentQuestionAnswer = (
  state: RootState
): QuestionnaireAnswer | undefined => {
  const currentQuestion = state.questionnaire.currentQuestion;
  if (currentQuestion) {
    const answer: any =
      { ...state.questionnaire.answers[currentQuestion.id] } ?? {};
    answer.patientId = state.user.patientId;
    return answer;
  }
  return undefined;
};
export const selectCurrentQuestionRequired = (state: RootState) => {
  const currentQuestion = state.questionnaire.currentQuestion;
  let require = false;
  if (currentQuestion) {
    if (currentQuestion.required) {
      require = true;
    } else if (
      currentQuestion.type.includes("group") &&
      currentQuestion.item &&
      currentQuestion.item.length
    ) {
      currentQuestion.item.forEach((subQuestion) => {
        if (subQuestion.required) {
          require = true;
        }
      });
    }
  }
  return require;
};
export const selectCurrentQuestionnaire = (state: RootState) => {
  return state.questionnaire.questionnaires.find(
    (elem) => elem.id === state.questionnaire.activeQuestionnaire
  );
};
export const selectStateStatus = (state: RootState) =>
  state.questionnaire.status;
export const selectQuestionnaireCompletePercent = (
  state: RootState,
  id: string
) => {
  return calculateCompletion(state.questionnaire, id);
};
export const selectActiveQuestionnaireCompletion = (
  state: RootState
): number => {
  const currentQuestionnaire = state.questionnaire.questionnaires.find(
    (elem) => elem.id === state.questionnaire.activeQuestionnaire
  );
  const possibleQuestions =
    currentQuestionnaire?.item.filter((elem) =>
      getIsEnabled(elem, state.questionnaire.answers)
    ) ?? [];
  let previousQuestions = [];
  const id: string = state.questionnaire.currentQuestion?.id ?? "";
  if (id !== "") {
    previousQuestions =
      currentQuestionnaire?.item.filter((elem) => {
        return (
          getIsEnabled(elem, state.questionnaire.answers) &&
          parseInt(elem.id) <= parseInt(id)
        );
      }) ?? [];
  }
  return Math.floor(
    (previousQuestions.length / possibleQuestions.length) * 100
  );
};
export const selectAllCompletionPercentages = (state: RootState) => {
  const ret: any = {};
  for (const questionnaire of state.questionnaire.questionnaires) {
    ret[questionnaire.id] = calculateCompletion(
      state.questionnaire,
      questionnaire.id
    );
  }
  return ret;
};

export const selectExtraQuestionnaires = (state: RootState) => {
  const defaultValues = Object.values(QUESTIONNAIRE_IDS);
  return state.questionnaire.questionnaires.filter(
    (elem) => !defaultValues.includes(elem.originId)
  );
};
export const selectQuestionnaireScore = (state: RootState, id: string) => {
  return calculateQuestionnaireScore(state.questionnaire, id);
};

export const selectResponsesGiven = (state: RootState) => {
  return state.questionnaire.responses.map((elem) => elem.questionnaire);
};

export const {
  reset,
  getNextQuestion,
  getPreviousQuestion,
  setActiveQuestionnaire,
  resetQuestionnaire,
  answerQuestion,
  updateHistoricalAnswers,
} = questionnaireSlice.actions;
export default questionnaireSlice.reducer;
